/**
 * AnalyticsPage — Deep performance analytics, all real data.
 *
 * Bloomberg/Fidelity-inspired sections computed from the userActivity
 * aggregator (single source of truth across CEX, Smart Trade, Algo, DEX).
 *
 * Sections:
 *   1. KPI row — total trades, volume, avg size, active days, best day, streak
 *   2. P&L honesty banner (cost-basis engine coming separately)
 *   3. Cumulative volume — 30-day running total
 *   4. Time-of-Day heatmap (7×24 grid showing when user actually trades)
 *   5. Trade size distribution histogram
 *   6. Per-strategy performance table
 *   7. Per-pair performance table (sortable)
 */

import { useMemo, useState } from 'react';
import {
  TrendingUp, BarChart3, Clock, Activity, Layers,
  Target, Calendar, Flame, Award, AlertCircle, RefreshCw,
} from 'lucide-react';
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip,
  BarChart, Bar, CartesianGrid,
} from 'recharts';
import { useUserActivity } from '../hooks/useData';
import { Spinner, InfoTip } from '../components/ui';
import { formatUsd } from '../services/api';
import { formatUsdCompact } from '../services/displayPrefs';
import { format } from 'date-fns';

// ─── Constants & helpers ────────────────────────────────────────────────────

const SOURCE_LABELS = {
  cex_manual: 'CEX Trading',
  cex_futures: 'Futures',
  dex_swap: 'DEX Swap',
  dex_limit: 'DEX Limit',
  smart_trade: 'Smart Trade',
  algo_twap: 'TWAP',
  algo_vwap: 'VWAP',
  algo_iceberg: 'Iceberg',
  algo_bracket: 'Bracket',
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function Kpi({ label, value, sub, accent = 'amber', icon: Icon, info }) {
  const colors = {
    amber: 'text-amber-400', emerald: 'text-emerald-400', red: 'text-red-400',
    cyan: 'text-cyan-400', violet: 'text-violet-400', blue: 'text-blue-400',
  };
  return (
    <div className="bg-dark-800 rounded-2xl p-5 border border-dark-700/50">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5">
          {label}
          {info && <InfoTip side="bottom">{info}</InfoTip>}
        </span>
        {Icon && <Icon className={`w-4 h-4 ${colors[accent]} opacity-70`} />}
      </div>
      <p className={`text-2xl font-bold font-mono ${colors[accent]}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, action, children, hint }) {
  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-amber-400" />} {title}
        </h3>
        {action || (hint && <span className="text-[10px] text-gray-500 italic">{hint}</span>)}
      </div>
      {children}
    </div>
  );
}

function TimeOfDayHeatmap({ matrix, volumeMatrix }) {
  const max = Math.max(...matrix.flat());
  const colorFor = (v) => {
    if (max === 0) return 'rgba(75, 85, 99, 0.2)';
    const intensity = v / max;
    return `rgba(245, 158, 11, ${0.05 + intensity * 0.85})`;
  };

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="flex gap-0.5 ml-10 mb-1">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="w-6 text-center text-[9px] text-gray-600 font-mono">
              {h % 4 === 0 ? `${h}h` : ''}
            </div>
          ))}
        </div>
        {matrix.map((row, dow) => (
          <div key={dow} className="flex gap-0.5 mb-0.5">
            <div className="w-10 text-[10px] text-gray-500 font-mono pr-2 flex items-center justify-end">{DAY_LABELS[dow]}</div>
            {row.map((cell, hour) => (
              <div
                key={hour}
                className="w-6 h-6 rounded flex items-center justify-center text-[8px] text-white"
                style={{ background: colorFor(cell) }}
                title={`${DAY_LABELS[dow]} ${hour}:00 UTC — ${cell} trade${cell === 1 ? '' : 's'}${volumeMatrix?.[dow]?.[hour] ? ` · $${Math.round(volumeMatrix[dow][hour]).toLocaleString()} volume` : ''}`}
              >
                {cell > 0 && cell <= 99 ? cell : cell > 99 ? '99+' : ''}
              </div>
            ))}
          </div>
        ))}
        <p className="text-[10px] text-gray-500 mt-3 italic">All times in UTC. Cell shows trade count; hover for exact volume.</p>
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

const RANGE_OPTIONS = [
  { id: 7,   label: '7d' },
  { id: 30,  label: '30d' },
  { id: 60,  label: '60d' },
  { id: 90,  label: '90d' },
  { id: 'all', label: 'All' },
];

export default function AnalyticsPage() {
  const [range, setRange] = useState(30);
  const { summary, loading, error, refresh } = useUserActivity({
    recent: 0,
    days: typeof range === 'number' ? range : 90,
    window: range === 'all' ? null : range,
  });
  const [pairSort, setPairSort] = useState('volume');

  const stats = useMemo(() => {
    if (!summary) return null;
    const all = summary.allTime || {};
    const ts = summary.timeSeries || [];
    const activeDays = ts.filter(d => d.trades > 0).length;
    const bestDay = ts.reduce((b, d) => d.volumeUsd > (b?.volumeUsd ?? -Infinity) ? d : b, null);
    const avgTradeSize = all.tradesCount > 0 ? all.volumeUsd / all.tradesCount : 0;

    let curStreak = 0, maxStreak = 0;
    for (const d of ts) {
      if (d.trades > 0) { curStreak++; if (curStreak > maxStreak) maxStreak = curStreak; }
      else curStreak = 0;
    }

    let running = 0;
    const cumulative = ts.map(d => {
      running += d.volumeUsd;
      return { date: d.date, cumVolume: Number(running.toFixed(2)), dailyVolume: d.volumeUsd, trades: d.trades };
    });

    return {
      totalTrades: all.tradesCount || 0,
      totalVolume: all.volumeUsd || 0,
      totalFees: all.feesUsd || 0,
      activeDays,
      bestDay,
      avgTradeSize,
      longestStreak: maxStreak,
      cumulative,
    };
  }, [summary]);

  const sortedPairs = useMemo(() => {
    if (!summary?.bySymbol) return [];
    const arr = [...summary.bySymbol];
    if (pairSort === 'volume') arr.sort((a, b) => b.volumeUsd - a.volumeUsd);
    else arr.sort((a, b) => b.tradesCount - a.tradesCount);
    return arr;
  }, [summary, pairSort]);

  if (loading) return <div className="flex items-center justify-center h-96"><Spinner size="lg" className="text-accent-primary" /></div>;
  if (error) return (
    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
      {error} <button onClick={refresh} className="ml-3 underline">Retry</button>
    </div>
  );

  const hasActivity = (summary?.counts?.total || 0) > 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">Trade Analytics</h1>
          <p className="text-gray-400 text-sm mt-1">
            Volume, timing, per-pair stats — all from your real trades. For institutional-grade alpha/beta/correlation, see Quant Desk.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-dark-800 rounded-lg p-0.5 border border-dark-700">
            {RANGE_OPTIONS.map(opt => (
              <button key={opt.id} onClick={() => setRange(opt.id)}
                className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-all ${range === opt.id ? 'bg-amber-400 text-dark-900' : 'text-gray-400 hover:text-white'}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <button onClick={refresh} className="text-xs text-gray-500 hover:text-white px-3 py-1.5 rounded-lg bg-dark-800 border border-dark-700 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {!hasActivity ? (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-white font-medium">No trades to analyze yet</p>
          <p className="text-gray-500 text-sm mt-1">Place your first trade and the full analytics suite populates automatically.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi label="Total Trades" value={stats.totalTrades.toLocaleString()} icon={Activity} accent="amber"
              info="Filled trades across every surface in the selected window. Pending limit orders, cancelled orders, and failed transactions do not count." />
            <Kpi label="Total Volume" value={formatUsd(stats.totalVolume)} icon={BarChart3} accent="cyan"
              info="USD notional summed across every trade in the window. A 100 USDC trade counts as $100 regardless of which surface executed it." />
            <Kpi label="Avg Trade Size" value={formatUsd(stats.avgTradeSize)} icon={Target} accent="emerald"
              info="Total Volume ÷ Total Trades. Rising avg = you're sizing up. Falling avg = more small-trade churn (often a profitability red flag)." />
            <Kpi label={range === 'all' ? 'Active Days (All)' : `Active Days (${range}d)`} value={`${stats.activeDays}/${range === 'all' ? '∞' : range}`} icon={Calendar} accent="violet"
              info="Days in the window where you placed at least one filled trade. Low ratio = passive or sporadic; high ratio = consistently engaged." />
            <Kpi
              label="Best Day" value={stats.bestDay ? formatUsd(stats.bestDay.volumeUsd) : '—'}
              sub={stats.bestDay ? format(new Date(stats.bestDay.date), 'MMM d') : null}
              icon={Award} accent="emerald"
              info="Your biggest single-day USD volume in the window. If it's 5×+ your typical day, ask whether it was planned or impulse."
            />
            <Kpi label="Longest Streak" value={`${stats.longestStreak}d`} icon={Flame} accent="red"
              info="Longest run of consecutive active days. A streak that broke at exactly N days is usually worth journaling — vacations, market events, or burnout patterns are visible here." />
          </div>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200/90 leading-relaxed">
              <p className="font-semibold text-amber-300 mb-1">P&L tracking — coming with the cost-basis engine</p>
              <p>
                Realized + unrealized P&L, Sharpe, Sortino, Calmar, and max drawdown require accurate cost-basis accounting (FIFO/LIFO/HIFO across all your accounts).
                That engine is in flight — until it lands, we show real volume and behavioral metrics rather than estimated P&L.
                We never fabricate financial numbers.
              </p>
            </div>
          </div>

          <Section title={range === 'all' ? 'Cumulative Volume — All Time' : `Cumulative Volume — ${range} Days`} icon={TrendingUp}>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={stats.cumulative} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#10b981" stopOpacity={0.5}/>
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="date" tickFormatter={d => format(new Date(d), 'MMM d')} stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false}
                  tickFormatter={formatUsdCompact} />
                <Tooltip
                  contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(v, n) => n === 'cumVolume' ? [`$${Number(v).toLocaleString()}`, 'Cumulative'] : [`$${Number(v).toLocaleString()}`, 'Daily']}
                  labelFormatter={d => format(new Date(d), 'MMM d, yyyy')}
                />
                <Area type="monotone" dataKey="cumVolume" stroke="#10b981" strokeWidth={2} fill="url(#cumGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </Section>

          <Section title="When You Trade — Time of Day Heatmap" icon={Clock}
            hint="Find your active hours. Some traders perform better at specific times.">
            <TimeOfDayHeatmap
              matrix={summary?.behavior?.tradeCountByDowHour || Array.from({ length: 7 }, () => Array(24).fill(0))}
              volumeMatrix={summary?.behavior?.volumeByDowHour}
            />
          </Section>

          <Section title="Trade Size Distribution" icon={BarChart3}
            hint="Are you a small frequent trader or a large infrequent trader?">
            {summary?.behavior?.sizeHistogram?.some(b => b.count > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={summary.behavior.sizeHistogram} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis dataKey="label" stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(v, n) => n === 'count' ? [`${v} trades`, 'Count'] : [`$${Number(v).toLocaleString()}`, 'Volume']}
                  />
                  <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-gray-500 py-8 text-center">Need more trades to build histogram.</p>}
          </Section>

          <Section title="Performance by Strategy" icon={Layers}>
            {summary?.bySource?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-dark-700/50">
                      <th className="py-2 font-medium">Strategy</th>
                      <th className="py-2 font-medium text-right">Trades</th>
                      <th className="py-2 font-medium text-right">Volume</th>
                      <th className="py-2 font-medium text-right">Avg Size</th>
                      <th className="py-2 font-medium text-right">Share</th>
                      <th className="py-2 font-medium text-right">Last Trade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.bySource.map(s => {
                      const avg = s.tradesCount > 0 ? s.volumeUsd / s.tradesCount : 0;
                      const share = stats.totalVolume > 0 ? (s.volumeUsd / stats.totalVolume * 100) : 0;
                      return (
                        <tr key={s.key} className="border-b border-dark-700/30 hover:bg-dark-700/20">
                          <td className="py-2.5 text-white font-medium">{SOURCE_LABELS[s.key] || s.key}</td>
                          <td className="py-2.5 text-right text-gray-300 font-mono">{s.tradesCount.toLocaleString()}</td>
                          <td className="py-2.5 text-right text-gray-300 font-mono">{formatUsd(s.volumeUsd)}</td>
                          <td className="py-2.5 text-right text-gray-300 font-mono">{formatUsd(avg)}</td>
                          <td className="py-2.5 text-right text-amber-400 font-mono">{share.toFixed(1)}%</td>
                          <td className="py-2.5 text-right text-gray-500 text-[11px]">{s.lastTs ? format(new Date(s.lastTs), 'MMM d, HH:mm') : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-xs text-gray-500 py-8 text-center">No strategy data yet.</p>}
          </Section>

          <Section title="Performance by Pair" icon={Target}
            action={
              <div className="inline-flex bg-dark-700 rounded-lg p-0.5 border border-dark-600">
                <button onClick={() => setPairSort('volume')} className={`px-2.5 py-1 rounded-md text-[10px] font-bold ${pairSort === 'volume' ? 'bg-amber-400 text-dark-900' : 'text-gray-500'}`}>Volume</button>
                <button onClick={() => setPairSort('count')} className={`px-2.5 py-1 rounded-md text-[10px] font-bold ${pairSort === 'count' ? 'bg-amber-400 text-dark-900' : 'text-gray-500'}`}>Count</button>
              </div>
            }>
            {sortedPairs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-dark-700/50">
                      <th className="py-2 font-medium">Pair</th>
                      <th className="py-2 font-medium text-right">Trades</th>
                      <th className="py-2 font-medium text-right">Volume</th>
                      <th className="py-2 font-medium text-right">Avg Size</th>
                      <th className="py-2 font-medium text-right">Share</th>
                      <th className="py-2 font-medium text-right">Last Trade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPairs.slice(0, 20).map(p => {
                      const avg = p.tradesCount > 0 ? p.volumeUsd / p.tradesCount : 0;
                      const share = stats.totalVolume > 0 ? (p.volumeUsd / stats.totalVolume * 100) : 0;
                      return (
                        <tr key={p.key} className="border-b border-dark-700/30 hover:bg-dark-700/20">
                          <td className="py-2.5 text-white font-mono">{p.key}</td>
                          <td className="py-2.5 text-right text-gray-300 font-mono">{p.tradesCount}</td>
                          <td className="py-2.5 text-right text-gray-300 font-mono">{formatUsd(p.volumeUsd)}</td>
                          <td className="py-2.5 text-right text-gray-300 font-mono">{formatUsd(avg)}</td>
                          <td className="py-2.5 text-right text-cyan-400 font-mono">{share.toFixed(1)}%</td>
                          <td className="py-2.5 text-right text-gray-500 text-[11px]">{p.lastTs ? format(new Date(p.lastTs), 'MMM d, HH:mm') : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-xs text-gray-500 py-8 text-center">No pair data yet.</p>}
          </Section>
        </>
      )}
    </div>
  );
}
